import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface FeaturedArticleData {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  imageAlt: string;
  author: string;
}

interface FeaturedArticleProps {
  article: FeaturedArticleData;
  onReadMore: (id: string) => void;
}

export default function FeaturedArticle({ article, onReadMore }: FeaturedArticleProps) {
  return (
    <article className="bg-card rounded-lg shadow-organic overflow-hidden border border-border">
      <div className="grid lg:grid-cols-2 gap-0">
        <div className="relative h-64 lg:h-full overflow-hidden">
          <AppImage
            src={article.image}
            alt={article.imageAlt}
            className="w-full h-full object-cover"
          />
          <div className="absolute top-4 left-4 bg-accent text-accent-foreground px-4 py-2 rounded-full text-sm font-cta font-semibold flex items-center space-x-2">
            <Icon name="FireIcon" size={18} variant="solid" />
            <span>Breaking News</span>
          </div>
        </div>
        
        <div className="p-8 lg:p-12 flex flex-col justify-center space-y-6">
          <div className="inline-flex items-center space-x-2 text-primary">
            <Icon name="NewspaperIcon" size={20} variant="solid" />
            <span className="text-sm font-cta font-semibold uppercase tracking-wide">{article.category}</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground leading-tight">
            {article.title}
          </h2>
          
          <p className="text-lg text-muted-foreground font-body leading-relaxed">
            {article.excerpt}
          </p>
          
          <div className="flex items-center space-x-6 text-sm text-muted-foreground font-body">
            <div className="flex items-center space-x-2">
              <Icon name="UserIcon" size={16} variant="outline" />
              <span>{article.author}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="CalendarIcon" size={16} variant="outline" />
              <span>{article.date}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="ClockIcon" size={16} variant="outline" />
              <span>{article.readTime}</span>
            </div>
          </div>
          
          <button
            onClick={() => onReadMore(article.id)}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground font-cta text-sm font-semibold rounded-md hover:bg-primary/90 shadow-organic-sm transition-organic duration-300 w-fit"
          >
            <span>Read Full Article</span>
            <Icon name="ArrowRightIcon" size={18} variant="outline" />
          </button>
        </div>
      </div>
    </article>
  );
}